import React, { FunctionComponent, ReactElement } from 'react';
import {
  ErrorMessage,
  Field,
  Form,
  Formik,
} from 'formik';
import {
  Col,
  Container,
  Row,
  Spinner,
} from 'react-bootstrap';
import ReactStars from 'react-rating-stars-component';
import * as Yup from 'yup';
import Select from 'react-select';
import { getComputedCSSVariable } from '../../utils/cssVariableHelpers';

interface FormValues {
  message: string,
  rating?: number,
  status?: string,
}

interface Props {
  formTitle: string,
  validator: Yup.ObjectSchema<any>,
  formValues: FormValues,
  handleClose?: () => void,
  onSubmit: (values: FormValues) => Promise<any>,
  loading: boolean
  canUpdateStatus?: boolean,
  FooterComponent?: FunctionComponent,
}

const SellerReviewForm: React.FC<Props> = ({
  formTitle,
  formValues,
  validator,
  onSubmit,
  handleClose,
  loading,
  canUpdateStatus,
  FooterComponent,
}): ReactElement => {
  return (
    <Formik
      enableReinitialize
      initialValues={formValues}
      validationSchema={validator}
      onSubmit={async (values) => onSubmit(values)}
    >
      {(formikProps) => {
        const {
          errors,
          touched,
          values,
          dirty,
          isValid,
          setFieldValue,
          setFieldTouched,
        } = formikProps;
        return (
          <Form id="sellerReviewForm" className="p-2 p-xl-4">
            <Container fluid>
              {handleClose && (
                <button type="button" className="close" onClick={handleClose} aria-label="Close">
                  <span aria-hidden="true">&times;</span>
                </button>
              )}
              <Row className="w-100">
                <Col md={12} lg={12}>
                  <div className="p-md-6 p-sm-3 p-1 row">
                    <h2 className="text-center text-blue-500">{formTitle}</h2>
                    <div className="w-100">
                      <Row className="mb-2">
                        <Col>
                          <ReactStars
                            count={5}
                            value={values.rating}
                            onChange={(newRating) => {
                              setFieldValue('rating', newRating);
                            }}
                            activeColor={getComputedCSSVariable('yellow')}
                            size={25}
                            isHalf={true}
                            color={getComputedCSSVariable('l-gray')}
                          />
                          <ErrorMessage name="rating" component="div" className="invalid-feedback" />
                        </Col>
                      </Row>
                      <Row className="mb-2">
                        <Col>
                          <Field
                            as="textarea"
                            name="message"
                            placeholder="Add a review message..."
                            className={`form-control${errors.message && touched.message ? ' is-invalid' : ''}`}
                          />
                          <ErrorMessage name="message" component="div" className="invalid-feedback" />
                        </Col>
                      </Row>
                      {canUpdateStatus && (
                        <Row>
                          <Col>
                            <Select
                              className={`mb-2 form-control${errors.status && touched.status ? ' is-invalid' : ''}`}
                              placeholder="Choose a status..."
                              options={[
                                {
                                  label: 'verified',
                                  value: 'verified',
                                },
                                {
                                  label: 'pending',
                                  value: 'pending',
                                },
                                {
                                  label: 'declined',
                                  value: 'declined',
                                },
                              ]}
                              isDisabled={!canUpdateStatus}
                              onChange={(e) => {
                                setFieldValue('status', e?.value);
                                setFieldTouched('status');
                              }}
                              value={values.status
                                ? {
                                  label: values.status,
                                  value: values.status,
                                }
                                : {}}
                            />
                            <ErrorMessage name="status" component="div" className="invalid-feedback" />
                          </Col>
                        </Row>
                      )}
                      <div className="form-group mb-0">
                        <button
                          type="submit"
                          className="btn btn-primary"
                          disabled={loading || !isValid || !dirty}
                        >
                          {
                            loading
                              ? (
                                <>
                                  <Spinner
                                    as="span"
                                    animation="border"
                                    size="sm"
                                    role="status"
                                    aria-hidden="true"
                                  />
                                  <span className="visually-hidden">Loading...</span>
                                </>
                              )
                              : (<>Submit</>)
                          }
                        </button>
                      </div>
                    </div>
                  </div>
                </Col>
              </Row>
            </Container>
            {FooterComponent && <FooterComponent />}
          </Form>
        );
      }}
    </Formik>
  );
};

export default SellerReviewForm;
